import type { PosteEntreprise } from "./api";

/**
 * Ce que le dirigeant a réellement déclaré, et les rangs qui vont avec.
 *
 * ## Le défaut que cette fonction ferme — 02/09/2026
 *
 * L'écran propose sept postes ; le dirigeant n'en remplit presque jamais sept.
 * Le profil envoyé au serveur ne porte que ceux dont les heures sont
 * strictement positives — c'est voulu, un poste à zéro heure n'a rien à dire au
 * rapport.
 *
 * Mais la question « sur lesquels de ces postes une IA sert-elle déjà ? »
 * enregistrait des rangs dans le tableau **complet**, et personne ne les
 * réindexait avant l'envoi. Le rapport à 15 000 F les relisait dans le tableau
 * **filtré** : il nommait donc un autre poste que celui désigné, ou le perdait
 * quand le rang tombait hors bornes — le serveur borne les rangs, ce qui a
 * masqué la moitié des cas au lieu de les signaler.
 *
 * Un poste à zéro heure devant un poste désigné suffisait à décaler toute la
 * suite. C'est la situation ordinaire, pas le cas limite : le paragraphe le
 * plus personnel du document nommait le mauvais poste, avec l'assurance d'un
 * document payé.
 *
 * ## Pourquoi une fonction, et pas deux lignes dans le composant
 *
 * Parce que la seule chose à vérifier est une correspondance entre deux
 * numérotations, et qu'une correspondance se prouve sur des tableaux, pas sur
 * un rendu React. Le composant l'appelle et n'en garde rien d'autre.
 */
export interface PosteDeclare {
  name: string;
  hoursPerWeek: number;
  auto: number;
}

export interface Declaration {
  /** Les postes retenus, dans l'ordre du tableau affiché. */
  postes: PosteDeclare[];
  /** Les rangs « une IA sert déjà ici », **dans `postes`** — triés, sans doublon. */
  postesAvecIa: number[];
}

export function declarerPostes(
  postes: readonly PosteEntreprise[],
  heures: Readonly<Record<number, number>>,
  rangsAvecIa: readonly number[],
): Declaration {
  const retenus: Array<{ poste: PosteDeclare; rangInitial: number }> = [];
  postes.forEach((poste, rangInitial) => {
    const hoursPerWeek = heures[rangInitial] ?? 0;
    if (hoursPerWeek > 0) {
      retenus.push({ poste: { name: poste.name, hoursPerWeek, auto: poste.auto }, rangInitial });
    }
  });

  /*
    Un rang qui ne correspond à aucun poste retenu est abandonné, sans bruit.

    C'est le cas du dirigeant qui désigne un poste, revient en arrière et remet
    ses heures à zéro : il a retiré le poste, il a donc retiré la réponse. Le
    garder ferait dire au rapport « une IA sert déjà » d'un poste absent du
    tableau que le lecteur a sous les yeux.
  */
  const reindexes = new Set<number>();
  for (const rang of rangsAvecIa) {
    const place = retenus.findIndex((r) => r.rangInitial === rang);
    if (place >= 0) reindexes.add(place);
  }

  return {
    postes: retenus.map((r) => r.poste),
    postesAvecIa: [...reindexes].sort((a, b) => a - b),
  };
}
